// Mirror of backend types/pages.ts — keep in sync.

export const EMPLOYEE_PAGES = [
  "employee:dashboard",
  "employee:payslip",
  "employee:loans",
  "employee:leave",
  "employee:coe",
  "employee:tasks",
  "employee:wallet",
  "employee:cash-advance",
  "employee:work-adjustments",
] as const;

export const ADMIN_PAGES = [
  "admin:dashboard",
  "admin:users",
  "admin:payslip",
  "admin:loans",
  "admin:leave",
  "admin:tasks",
  "admin:attendance",
  "admin:adjustments",
  "admin:wallet",
  "admin:cash-advance",
  "admin:reports",
  "admin:ledger",
  "admin:work-adjustments",
  "admin:financials",
] as const;

export const STORE_PAGES = [
  "store:manage-store",
] as const;

export const ASSIGNABLE_PAGES = [
  ...EMPLOYEE_PAGES,
  ...ADMIN_PAGES,
  ...STORE_PAGES,
] as const;

export type PageKey = (typeof ASSIGNABLE_PAGES)[number];

export const PAGE_LABELS: Record<PageKey, string> = {
  // Employee
  "employee:dashboard": "Employee Dashboard",
  "employee:payslip": "Payslip",
  "employee:loans": "Loan Application",
  "employee:leave": "Leave Application",
  "employee:coe": "Certificate of Employment",
  "employee:tasks": "My Tasks",
  "employee:wallet": "My Wallet",
  "employee:cash-advance": "Cash Advance",
  "employee:work-adjustments": "Work Time Claims",
  // Admin
  "admin:dashboard": "Admin Dashboard",
  "admin:users": "Manage Users",
  "admin:payslip": "Process Payslip",
  "admin:loans": "Manage Loans",
  "admin:leave": "Manage Leave",
  "admin:tasks": "Manage Tasks",
  "admin:attendance": "Attendance",
  "admin:adjustments": "Payslip Adjustments",
  "admin:wallet": "Manage Wallet",
  "admin:cash-advance": "Manage Cash Advance",
  "admin:reports": "Reports",
  "admin:ledger": "Transaction Ledger",
  "admin:work-adjustments": "Work Time Claims",
  "admin:financials": "Financial Dashboard",
  // Store
  "store:manage-store": "Manage Store",
};

export const PAGE_CATEGORIES: {
  label: string;
  pages: readonly PageKey[];
}[] = [
  { label: "Employee Pages", pages: EMPLOYEE_PAGES },
  { label: "Admin Pages", pages: ADMIN_PAGES },
  { label: "Store Pages", pages: STORE_PAGES },
];

// ── Approval action controls ──────────────────────────────────────────────
// Mirror of backend types/pages.ts — keep in sync.
export const APPROVAL_ACTIONS = ["approve", "reject", "release"] as const;
export type ApprovalAction = (typeof APPROVAL_ACTIONS)[number];

export const APPROVAL_SCOPES = {
  "loans": { page: "admin:loans", actions: ["approve", "reject", "release"] },
  "leave": { page: "admin:leave", actions: ["approve", "reject"] },
  "cash-advance": { page: "admin:cash-advance", actions: ["approve", "reject", "release"] },
  "work-adjustments": { page: "admin:work-adjustments", actions: ["approve", "reject"] },
  "wallet:encashment": { page: "admin:wallet", actions: ["approve", "reject", "release"] },
  "wallet:advance": { page: "admin:wallet", actions: ["approve", "reject", "release"] },
  "wallet:service-request": { page: "admin:wallet", actions: ["approve", "reject"] },
} as const satisfies Record<string, { page: PageKey; actions: readonly ApprovalAction[] }>;

export type ApprovalScope = keyof typeof APPROVAL_SCOPES;

export const APPROVAL_SCOPE_KEYS = Object.keys(APPROVAL_SCOPES) as ApprovalScope[];

export const APPROVAL_SCOPE_LABELS: Record<ApprovalScope, string> = {
  "loans": "Loans",
  "leave": "Leave",
  "cash-advance": "Cash Advance",
  "work-adjustments": "Work Time Claims",
  "wallet:encashment": "Wallet — Encashment",
  "wallet:advance": "Wallet — Advances",
  "wallet:service-request": "Wallet — Service Requests",
};

export const APPROVAL_ACTION_LABELS: Record<ApprovalAction, string> = {
  approve: "Approve",
  reject: "Reject",
  release: "Release",
};
